// Диспетчер action-карт Деуса (А.4.4.3).
// Action-карты идут из колоды → в руку → в Ф2 ставятся в action-слот → в Ф3 резолвятся первыми.
//
// API:
//   needsTarget(cardId) → bool
//   getTargets(state, playerIdx, cardId) → массив Card-целей или []
//   resolveAction(state, playerIdx, cardId, targetCard?) → {ok}
//
// Действия: Взлом (цель=враг.юнит), Перегруппировка (цель=любой юнит), Манипуляция (без цели).

import { CARD_ID as ID_VZLOM, getVzlomTargets, resolveVzlom } from './vzlom.js';
import { CARD_ID as ID_PERE,  getPeregruppirovkaTargets, resolvePeregruppirovka } from './peregruppirovka.js';
import { CARD_ID as ID_MANIP, resolveManipulyatsiya } from './manipulyatsiya.js';
// C.1 Этап 33: новые v6 Действия Деуса.
import { applyDamage } from '../../combat.js';
import { dispatchUnitDied } from './hooks.js';
import { summonUnit } from '../global/summon.js';
import { mindControlUnit } from '../../effects/mind-control.js';
// C.1 Этап 34: ВСЕ 23 v6 Действия Деуса в одном файле c1/actions-c1.js.
import { isC1Action, needsTargetC1, getActionTargetsC1, resolveActionC1 } from './c1/actions-c1.js';

const ID_VYGOVOR = 'deus_vygovor';
const ID_VRAZHD  = 'deus_vrazhdebnoe_pogloshchenie';
const STAZHER_ID = 'deus_stazher';

const TARGETED = new Set([ID_VZLOM, ID_PERE, ID_VYGOVOR, ID_VRAZHD]);

export function actionNeedsTarget(cardId) {
  if (isC1Action(cardId)) return needsTargetC1(cardId);
  return TARGETED.has(cardId);
}

export function getActionTargets(state, playerIdx, cardId) {
  if (isC1Action(cardId)) return getActionTargetsC1(state, playerIdx, cardId);
  switch (cardId) {
    case ID_VZLOM: return getVzlomTargets(state, playerIdx);
    case ID_PERE:  return getPeregruppirovkaTargets(state, playerIdx);
    case ID_MANIP: return [];
    case ID_VYGOVOR: {
      // Любой вражеский юнит.
      const enemy = state.players[1 - playerIdx];
      const r = [];
      for (const row of enemy.field) for (const u of row || []) if (u) r.push(u);
      return r;
    }
    case ID_VRAZHD: {
      // Вражеские юниты с atk ≤ 3.
      const enemy = state.players[1 - playerIdx];
      const r = [];
      for (const row of enemy.field) {
        for (const u of row || []) {
          if (u && (u.current?.atk ?? u.atk ?? 0) <= 3) r.push(u);
        }
      }
      return r;
    }
    default: return [];
  }
}

export function resolveAction(state, playerIdx, cardId, targetCard = null, opts = {}) {
  if (isC1Action(cardId)) {
    return resolveActionC1(state, playerIdx, cardId, targetCard, opts);
  }
  switch (cardId) {
    case ID_VZLOM: return resolveVzlom(state, playerIdx, targetCard);
    case ID_PERE:  return resolvePeregruppirovka(state, playerIdx, targetCard);
    case ID_MANIP: return resolveManipulyatsiya(state, playerIdx);
    case ID_VYGOVOR: return resolveVygovor(state, playerIdx, targetCard);
    case ID_VRAZHD:  return resolveVrazhdebnoe(state, playerIdx, targetCard);
    default:       return { ok: false, reason: 'unknown_action' };
  }
}

// C.1 Этап 33 — Выговор (common): 2 урона выбранному вражескому юниту +
// призыв Стажёра на свою сторону. Минимальная реализация — на ту же
// линию что и цель (если занято — другая; если обе по 4 — призыв отменяется).
function resolveVygovor(state, playerIdx, targetCard) {
  const me = state.players[playerIdx];
  const opponent = state.players[1 - playerIdx];
  if (!targetCard) return { ok: false, reason: 'no_target' };
  const before = { hp: targetCard.hp, armor: targetCard.armor };
  applyDamage(targetCard, 2, { pierce: false, state, source: { id: ID_VYGOVOR } });
  state.log.add(`📋 Выговор → ${targetCard.name_ru} [${opponent.name}]: 2 урона (HP ${before.hp}→${targetCard.hp}, броня ${before.armor}→${targetCard.armor})`);
  if (targetCard.hp <= 0) {
    opponent.removeFromField(targetCard);
    state.log.add(`✗ ${targetCard.name_ru} [${opponent.name}] погибает`);
    dispatchUnitDied(state, { ownerIdx: opponent.id, card: targetCard, killer: null, killerIdx: playerIdx, killerWasCounter: false });
  }
  // Призыв Стажёра. Предпочитаем ту же линию что у цели; затем другую.
  const preferLine = targetCard.line ?? 0;
  const lines = [preferLine, 1 - preferLine];
  for (const line of lines) {
    const row = me.field?.[line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) {
        summonUnit(state, playerIdx, STAZHER_ID, line, s);
        return { ok: true };
      }
    }
  }
  state.log.add(`📋 Выговор: нет места для Стажёра, призыв отменён`);
  return { ok: true, summoned: false };
}

// C.1 Этап 33 — Враждебное поглощение (legendary, ex-Кироса): переманить
// до 3 вражеских юнитов с atk ≤ 3 на свою сторону. Минимальная реализация:
// угоняет указанную цель, потом ещё до 2 случайных подходящих врагов,
// сколько свободных ячеек хватит.
function resolveVrazhdebnoe(state, playerIdx, targetCard) {
  const me = state.players[playerIdx];
  const enemy = state.players[1 - playerIdx];
  if (!targetCard) return { ok: false, reason: 'no_target' };
  let stolen = 0;
  const tryStealOne = (unit) => {
    if (!me.hasFreeSlot()) return false;
    const before = { line: unit.line, slot: unit.slot };
    const r = mindControlUnit(state, playerIdx, unit);
    if (r?.ok) {
      stolen++;
      state.log.add(`💼 Враждебное поглощение: ${unit.name_ru} переманен с (${before.line + 1},${before.slot + 1}) на сторону ${me.name}`);
      return true;
    }
    return false;
  };
  tryStealOne(targetCard);
  // Кандидаты — оставшиеся враги с atk ≤ 3.
  const remaining = [];
  for (const row of enemy.field) {
    for (const u of row || []) {
      if (u && u !== targetCard && (u.current?.atk ?? u.atk ?? 0) <= 3) remaining.push(u);
    }
  }
  // Случайно выбираем до (3 - stolen) дополнительных.
  for (let i = remaining.length - 1; i > 0; i--) {
    const j = state.rng?.int ? state.rng.int(i + 1) : Math.floor(Math.random() * (i + 1));
    [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
  }
  for (const u of remaining) {
    if (stolen >= 3) break;
    if (!me.hasFreeSlot()) break;
    tryStealOne(u);
  }
  state.log.add(`💼 Враждебное поглощение: всего переманено ${stolen} юнит(ов)`);
  return { ok: true, stolen };
}
